import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

/* --- colour palette --- */
const C = {
  accent: '#025c4f', accentLt: '#e8f5f0', accentDk: '#013d34',
  blue: '#1565c0', blueLt: '#e3f2fd',
  purple: '#6a1b9a', purpleLt: '#f3e5f5',
  orange: '#e65100', orangeLt: '#fff3e0',
  red: '#c62828', redLt: '#ffebee',
  green: '#2e7d32', greenLt: '#e8f5e9',
  gray: '#616161', grayLt: '#f5f5f5',
  border: '#e0e0e0', bg: '#fafafa',
};

/* helper set for het codes */
const HET_CODES = new Set(['K', 'R', 'S', 'W', 'Y', 'M']);

/* Copy-to-clipboard helper */
const copyTableToClipboard = (headers, rows) => {
  const head = headers.join('\t');
  const body = rows.map(r => r.join('\t')).join('\n');
  navigator.clipboard.writeText(head + '\n' + body);
};

/* STYLES (moved above components so they can reference S) */
const S = {
  section: {
    background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12,
    padding: 24, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.04)',
  },
  sectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    cursor: 'default',
  },
  cardGrid: {
    display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16,
  },
  statCard: {
    background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 10,
    padding: '16px 20px', minWidth: 130, textAlign: 'center', flex: '1 1 130px',
  },
  label: {
    display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4, color: '#333',
  },
  select: {
    width: '100%', padding: '9px 12px', border: '1px solid #e0e0e0',
    borderRadius: 8, fontSize: 14, background: '#fff',
  },
  input: {
    width: '100%', padding: '9px 12px', border: '1px solid #e0e0e0',
    borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    background: '#025c4f', color: '#fff', padding: '8px 10px', textAlign: 'left',
    position: 'sticky', top: 0, whiteSpace: 'nowrap', zIndex: 2,
  },
  td: { padding: '6px 10px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' },
  desc: { color: '#616161', fontSize: 14, margin: '0 0 12px', lineHeight: 1.5 },
  alert: { padding: '10px 16px', borderRadius: 8, fontSize: 14 },
  searchResults: {
    border: '1px solid #e0e0e0', borderRadius: 8, marginTop: 6,
    maxHeight: 280, overflowY: 'auto', background: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,.08)',
  },
  searchItem: {
    padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    transition: 'background .1s', fontSize: 13,
  },
};

/* ── Reusable sub-components (defined OUTSIDE main component to preserve identity across renders) ── */

const StatCard = ({ label, value, sub, color = C.accent, icon }) => (
  <div style={S.statCard}>
    {icon && <div style={{ fontSize: 28, marginBottom: 4 }}>{icon}</div>}
    <div style={{ fontSize: 28, fontWeight: 800, color: color, lineHeight: 1 }}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </div>
    <div style={{ fontSize: 12, color: C.gray, marginTop: 4, fontWeight: 600 }}>{label}</div>
    {sub && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{sub}</div>}
  </div>
);

const Section = ({ title, children, icon, collapsible, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={S.section}>
      <div style={S.sectionHeader} onClick={() => collapsible && setOpen(!open)}>
        <h3 style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          {icon && <span>{icon}</span>}
          {title}
        </h3>
        {collapsible && <span style={{ cursor: 'pointer', fontSize: 18 }}>{open ? '\u25BE' : '\u25B8'}</span>}
      </div>
      {(!collapsible || open) && <div style={{ marginTop: 16 }}>{children}</div>}
    </div>
  );
};

const Badge = ({ text, color = C.accent }) => (
  <span style={{
    background: color, color: '#fff', padding: '3px 10px',
    borderRadius: 12, fontSize: 11, fontWeight: 700, marginLeft: 4,
  }}>{text}</span>
);

const Btn = ({ children, onClick, disabled, variant = 'primary', style: extra = {} }) => {
  const base = {
    padding: '10px 22px', fontSize: 14, fontWeight: 600, border: 'none',
    borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all .15s', opacity: disabled ? 0.6 : 1,
    ...extra,
  };
  const variants = {
    primary: { ...base, background: C.accent, color: '#fff' },
    blue:    { ...base, background: C.blue, color: '#fff' },
    outline: { ...base, background: '#fff', color: C.accent, border: '2px solid ' + C.accent },
    danger:  { ...base, background: C.red, color: '#fff' },
    ghost:   { ...base, background: 'transparent', color: C.gray, border: '1px solid ' + C.border },
  };
  return <button onClick={onClick} disabled={disabled} style={variants[variant] || variants.primary}>{children}</button>;
};

const CopyBtn = ({ headers, rows, onClick, copied }) => (
  <button onClick={onClick || (() => {})}
    title="Copy table to clipboard"
    style={{
      background: copied ? C.greenLt : C.grayLt, border: '1px solid ' + C.border,
      borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12,
      fontWeight: 600, color: copied ? C.green : C.gray, transition: 'all .2s',
    }}>
    {copied ? '\u2705 Copied!' : '\u{1F4CB} Copy'}
  </button>
);

const BarChart = ({ data, maxWidth = 400 }) => {
  if (!data || data.length === 0) return null;
  const mx = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ maxWidth: maxWidth }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ fontSize: 11, width: 90, textAlign: 'right', color: C.gray, fontFamily: 'monospace' }}>
            {d.bin_start.toFixed(2)}-{d.bin_end.toFixed(2)}
          </span>
          <div style={{
            height: 16, background: C.accent,
            width: ((d.count / mx) * 100) + '%', minWidth: d.count > 0 ? 2 : 0,
            borderRadius: 3, transition: 'width .3s',
          }} />
          <span style={{ fontSize: 11, color: C.gray }}>{d.count}</span>
        </div>
      ))}
    </div>
  );
};

const TableSelect = ({ value, onChange, label = 'Select Table', width = '100%', tables }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={S.label}>{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)} style={{ ...S.select, maxWidth: width }}>
      <option value="">-- Select a table --</option>
      {tables.map(t => <option key={t} value={t}>{t}</option>)}
    </select>
  </div>
);

const Pagination = ({ data, onPageChange }) => {
  if (!data || data.total_pages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, justifyContent: 'center' }}>
      <Btn variant="ghost" disabled={data.page <= 1}
        onClick={() => onPageChange(data.page - 1)}
        style={{ padding: '6px 14px', fontSize: 13 }}>{'\u25C0'} Prev</Btn>
      <span style={{ fontSize: 13, color: C.gray }}>
        Page {data.page} of {data.total_pages} ({data.total.toLocaleString()} total)
      </span>
      <Btn variant="ghost" disabled={data.page >= data.total_pages}
        onClick={() => onPageChange(data.page + 1)}
        style={{ padding: '6px 14px', fontSize: 13 }}>Next {'\u25B6'}</Btn>
    </div>
  );
};

/* ================================================================
   SNP Tools  -  Genomic Breeding Database Toolkit
   ================================================================
   Provides easy access to SNP chip data for molecular breeders:

   • Dashboard    – Table overview, sample counts, RAD/year breakdown, sample roster
   • Data Access  – Point lookup (cultivar × marker), search, marker browser, CSV download
   • Compare      – Side-by-side genotype comparison of 2–10 cultivars, difference finder
   • QC           – Missing data, MAF distribution, het rates, diversity stats, concordance
   • Duplicates   – Within-table and cross-table near-identical genotype detection
   ================================================================ */
const SNPTools = ({ crop }) => {
  /* shared state */
  const [tables, setTables]               = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [activeTab, setActiveTab]         = useState('dashboard');
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [copied, setCopied]              = useState(false);

  /* Dashboard */
  const [overview, setOverview] = useState(null);

  /* Sample Roster */
  const [rosterData, setRosterData]     = useState(null);
  const [, setRosterPage]     = useState(1);
  const [rosterSearch, setRosterSearch] = useState('');
  const [rosterRad, setRosterRad]       = useState('');
  const [rosterYear, setRosterYear]     = useState('');

  /* Data Access */
  const [markerSearchQ, setMarkerSearchQ]     = useState('');
  const [markerSearchRes, setMarkerSearchRes] = useState([]);
  const [sampleSearchQ, setSampleSearchQ]     = useState('');
  const [sampleSearchRes, setSampleSearchRes] = useState([]);
  const [sampleLookup, setSampleLookup]       = useState(null);
  const [markerLookup, setMarkerLookup]       = useState(null);
  const [dlMarkerInput, setDlMarkerInput] = useState('');
  const [dlSampleInput, setDlSampleInput] = useState('');
  const [dlRadFilter, setDlRadFilter]     = useState('');
  const [downloading, setDownloading]     = useState(false);

  /* Point Lookup */
  const [pointSample, setPointSample]       = useState('');
  const [pointMarker, setPointMarker]       = useState('');
  const [pointResult, setPointResult]       = useState(null);

  /* Marker Browser */
  const [markerPrefixes, setMarkerPrefixes] = useState(null);
  const [browsePrefix, setBrowsePrefix]     = useState('');
  const [browseSearch, setBrowseSearch]     = useState('');
  const [, setBrowsePage]         = useState(1);
  const [browseData, setBrowseData]         = useState(null);

  /* Compare */
  const [compareSamplesInput, setCompareSamplesInput] = useState('');
  const [compareMax, setCompareMax]         = useState(5000);
  const [compareResults, setCompareResults] = useState(null);
  const [diffMode, setDiffMode]             = useState(false);

  /* Quality Control */
  const [profResults, setProfResults] = useState(null);
  const [profMax, setProfMax]         = useState(2000);
  const [missingResults, setMissingResults] = useState(null);
  const [mafResults, setMafResults]   = useState(null);
  const [divResults, setDivResults]       = useState(null);
  const [concMarkerInput, setConcMarkerInput] = useState('');
  const [concResults, setConcResults]     = useState(null);

  /* Duplicates */
  const [dupThreshold, setDupThreshold]   = useState(0.95);
  const [dupMaxMarkers, setDupMaxMarkers] = useState(2000);
  const [dupResults, setDupResults]       = useState(null);
  const [targetTable, setTargetTable]     = useState('');
  const [crossResults, setCrossResults]   = useState(null);

  const debounceRef = useRef(null);

  /* copy flash */
  const flashCopy = (headers, rows) => {
    copyTableToClipboard(headers, rows);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  /* fetch tables on mount */
  useEffect(() => {
    (async () => {
      try {
        let url = '/api/snp-tools/tables';
        if (crop) url += '?crop=' + encodeURIComponent(crop);
        const res = await api.get(url);
        setTables(res.data.tables || []);
      } catch (e) { setError('Failed to load tables'); }
    })();
  }, [crop]);

  /* reset state when table changes */
  useEffect(() => {
    setOverview(null); setSampleLookup(null); setMarkerLookup(null);
    setProfResults(null); setMissingResults(null); setMafResults(null);
    setDupResults(null); setCrossResults(null); setDivResults(null);
    setConcResults(null); setCompareResults(null); setPointResult(null);
    setRosterData(null); setMarkerPrefixes(null); setBrowseData(null);
    if (selectedTable && activeTab === 'dashboard') loadOverview();
    // eslint-disable-next-line
  }, [selectedTable]);

  useEffect(() => {
    if (selectedTable && activeTab === 'dashboard' && !overview) loadOverview();
    // eslint-disable-next-line
  }, [activeTab]);

  const loadOverview = async () => {
    if (!selectedTable) return;
    setLoading(true); setError('');
    try {
      const res = await api.get('/api/snp-tools/table-overview/' + selectedTable);
      setOverview(res.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  /* generic POST runner */
  const run = useCallback(async (url, body, setter) => {
    setLoading(true); setError('');
    try {
      const res = await api.post(url, body, { timeout: 600000 });
      setter(res.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, []);

  /* search helpers */
  const searchMarkers = async (q) => {
    if (!selectedTable || !q.trim()) { setMarkerSearchRes([]); return; }
    try {
      const res = await api.get('/api/snp-tools/search-markers/' + selectedTable + '?q=' + encodeURIComponent(q) + '&limit=30');
      setMarkerSearchRes(res.data.markers || []);
    } catch (e) { /* ignore */ }
  };
  const searchSamples = async (q) => {
    if (!selectedTable || !q.trim()) { setSampleSearchRes([]); return; }
    try {
      const res = await api.get('/api/snp-tools/search-samples/' + selectedTable + '?q=' + encodeURIComponent(q) + '&limit=30');
      setSampleSearchRes(res.data.samples || []);
    } catch (e) { /* ignore */ }
  };

  const debounceSearch = (fn, q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fn(q), 300);
  };

  const lookupSample = async (sid) => {
    setLoading(true); setError(''); setMarkerLookup(null);
    try {
      const res = await api.get('/api/snp-tools/sample-lookup/' + selectedTable + '/' + encodeURIComponent(sid));
      setSampleLookup(res.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  const lookupMarker = async (mk) => {
    setLoading(true); setError(''); setSampleLookup(null);
    try {
      const res = await api.get('/api/snp-tools/marker-lookup/' + selectedTable + '/' + encodeURIComponent(mk));
      setMarkerLookup(res.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  /* point lookup */
  const handlePointLookup = async () => {
    if (!selectedTable || !pointSample.trim() || !pointMarker.trim()) return;
    setLoading(true); setError('');
    try {
      const res = await api.get('/api/snp-tools/point-lookup/' +
        encodeURIComponent(selectedTable) + '/' +
        encodeURIComponent(pointSample.trim()) + '/' +
        encodeURIComponent(pointMarker.trim()));
      setPointResult(res.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  /* sample roster */
  const loadRoster = async (page = 1, search = rosterSearch, rad = rosterRad, year = rosterYear) => {
    if (!selectedTable) return;
    setLoading(true); setError('');
    try {
      let url = '/api/snp-tools/sample-roster/' + encodeURIComponent(selectedTable) +
        '?page=' + page + '&page_size=50';
      if (search) url += '&q=' + encodeURIComponent(search);
      if (rad) url += '&rad=' + encodeURIComponent(rad);
      if (year) url += '&year=' + encodeURIComponent(year);
      const res = await api.get(url);
      setRosterData(res.data);
      setRosterPage(page);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  /* marker browser */
  const loadPrefixes = async () => {
    if (!selectedTable) return;
    setLoading(true); setError('');
    try {
      const res = await api.get('/api/snp-tools/marker-prefixes/' + encodeURIComponent(selectedTable));
      setMarkerPrefixes(res.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  const loadBrowseMarkers = async (page = 1, prefix = browsePrefix, search = browseSearch) => {
    if (!selectedTable) return;
    setLoading(true); setError('');
    try {
      let url = '/api/snp-tools/browse-markers/' + encodeURIComponent(selectedTable) +
        '?page=' + page + '&page_size=100';
      if (prefix) url += '&prefix=' + encodeURIComponent(prefix);
      if (search) url += '&q=' + encodeURIComponent(search);
      const res = await api.get(url);
      setBrowseData(res.data);
      setBrowsePage(page);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  /* download subset */
  const handleSubsetDownload = async () => {
    if (!selectedTable) return;
    setDownloading(true); setError('');
    try {
      const markers = dlMarkerInput.trim()
        ? dlMarkerInput.split(/[,\n]+/).map(s => s.trim()).filter(Boolean)
        : null;
      const samples = dlSampleInput.trim()
        ? dlSampleInput.split(/[,\n]+/).map(s => s.trim()).filter(Boolean)
        : null;
      const body = {
        table_name: selectedTable,
        marker_names: markers,
        sample_ids: samples,
        rad_filter: dlRadFilter || null,
        include_metadata: true,
      };
      const res = await api.post('/api/snp-tools/download-subset-csv', body,
        { responseType: 'blob', timeout: 600000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedTable + '_subset.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { setError('Download failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setDownloading(false); }
  };

  /* compare */
  const handleCompare = async () => {
    if (!selectedTable) return;
    const names = compareSamplesInput.split(/[,\n]+/).map(s => s.trim()).filter(Boolean);
    if (names.length < 2) { setError('Enter at least 2 sample names'); return; }
    if (names.length > 10) { setError('Maximum 10 samples allowed'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.post('/api/snp-tools/compare-samples', {
        table_name: selectedTable,
        sample_names: names,
        max_markers: compareMax,
        only_differences: diffMode,
      }, { timeout: 600000 });
      setCompareResults(res.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  /* TAB DEFINITIONS */
  const TABS = [
    { key: 'dashboard',  icon: '\u{1F4CA}', label: 'Dashboard' },
    { key: 'access',     icon: '\u{1F50D}', label: 'Data Access' },
    { key: 'compare',    icon: '\u{1F504}', label: 'Compare' },
    { key: 'qc',         icon: '\u2705',    label: 'Quality Control' },
    { key: 'duplicates', icon: '\u{1F501}', label: 'Duplicates' },
  ];

  /* ======== 1. DASHBOARD ======== */
  const renderDashboard = () => (
    <div>
      {!selectedTable && (
        <Section title="Select a table to view dashboard" icon={'\u{1F4CA}'}>
          <p style={{ color: C.gray }}>Choose a genotype table from the dropdown above to see an overview of your SNP data.</p>
        </Section>
      )}

      {selectedTable && loading && !overview && (
        <div style={{ textAlign: 'center', padding: 40, color: C.gray }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{'\u23F3'}</div>
          Loading table overview...
        </div>
      )}

      {overview && (
        <>
          <div style={S.cardGrid}>
            <StatCard icon={'\u{1F9EC}'} label="Total Samples" value={overview.total_samples} />
            <StatCard icon={'\u{1F52C}'} label="Total Markers" value={overview.total_markers} color={C.blue} />
            {overview.quality_snapshot && overview.quality_snapshot.mean_call_rate != null && (
              <StatCard icon={'\u2705'} label="Avg Call Rate"
                value={((overview.quality_snapshot.mean_call_rate * 100).toFixed(1)) + '%'}
                color={overview.quality_snapshot.mean_call_rate > 0.95 ? C.green : C.orange} />
            )}
            {overview.quality_snapshot && overview.quality_snapshot.mean_het_rate != null && (
              <StatCard icon={'\u{1F504}'} label="Avg Het Rate"
                value={((overview.quality_snapshot.mean_het_rate * 100).toFixed(1)) + '%'}
                color={C.purple} />
            )}
          </div>

          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 20 }}>
            <Section title="Row Type Distribution" icon={'\u{1F33E}'}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {Object.entries(overview.rad_breakdown).map(([rad, count]) => (
                  <div key={rad} style={{
                    background: rad === '2' ? C.blueLt : rad === '6' ? C.orangeLt : C.grayLt,
                    padding: '12px 20px', borderRadius: 10, textAlign: 'center', minWidth: 100,
                  }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: rad === '2' ? C.blue : rad === '6' ? C.orange : C.gray }}>
                      {count.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 12, color: C.gray }}>
                      {rad === '2' ? '2-Row' : rad === '6' ? '6-Row' : 'RAD: ' + rad}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Year Distribution" icon={'\u{1F4C5}'}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(overview.year_breakdown).map(([year, count]) => (
                  <div key={year} style={{
                    background: C.grayLt, padding: '8px 14px', borderRadius: 8,
                    textAlign: 'center', fontSize: 13,
                  }}>
                    <strong>{year}</strong>
                    <div style={{ fontSize: 11, color: C.gray }}>{count} samples</div>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          <Section title="Marker Overview" icon={'\u{1F52C}'} collapsible defaultOpen={false}>
            <div style={{ fontSize: 13, color: C.gray }}>
              <p><strong>First markers:</strong> {overview.first_markers.join(', ')}</p>
              {overview.last_markers.length > 0 && (
                <p><strong>Last markers:</strong> {overview.last_markers.join(', ')}</p>
              )}
            </div>
          </Section>

          <Section title="Quick Actions" icon={'\u26A1'}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Btn onClick={() => setActiveTab('access')}>{'\u{1F50D}'} Search Data</Btn>
              <Btn onClick={() => setActiveTab('compare')} variant="blue">{'\u{1F504}'} Compare Cultivars</Btn>
              <Btn onClick={() => setActiveTab('qc')} variant="outline">{'\u2705'} Run QC</Btn>
              <Btn onClick={() => setActiveTab('duplicates')} variant="ghost">{'\u{1F501}'} Check Duplicates</Btn>
            </div>
          </Section>

          {/* Sample Roster */}
          <Section title="Sample Roster (Inventory)" icon={'\u{1F4CB}'} collapsible defaultOpen={false}>
            <p style={S.desc}>Instant, searchable inventory of all samples in this table.</p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'end' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={S.label}>Search</label>
                <input value={rosterSearch} placeholder="Type cultivar name..."
                  onChange={e => setRosterSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadRoster(1, rosterSearch, rosterRad, rosterYear)}
                  style={{ ...S.input, maxWidth: 300 }} />
              </div>
              {rosterData && rosterData.available_rads && (
                <div>
                  <label style={S.label}>RAD</label>
                  <select value={rosterRad} onChange={e => { setRosterRad(e.target.value); }}
                    style={{ ...S.select, maxWidth: 120 }}>
                    <option value="">All</option>
                    {rosterData.available_rads.map(r => <option key={r} value={r}>{r}-row</option>)}
                  </select>
                </div>
              )}
              {rosterData && rosterData.available_years && (
                <div>
                  <label style={S.label}>Year</label>
                  <select value={rosterYear} onChange={e => { setRosterYear(e.target.value); }}
                    style={{ ...S.select, maxWidth: 120 }}>
                    <option value="">All</option>
                    {rosterData.available_years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              )}
              <Btn disabled={!selectedTable || loading}
                onClick={() => loadRoster(1, rosterSearch, rosterRad, rosterYear)}>
                {loading ? 'Loading...' : '\u{1F4CB} Load Roster'}
              </Btn>
            </div>

            {rosterData && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: C.gray }}>
                    <strong>{rosterData.total.toLocaleString()}</strong> samples found
                  </span>
                  <CopyBtn copied={copied}
                    onClick={() => flashCopy(['Name', 'Alias', 'RAD', 'Year', 'Comment'],
                      rosterData.samples.map(s => [s.name, s.alias, s.rad, s.year, s.kommentar]))} />
                </div>
                <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
                  <table style={S.table}>
                    <thead><tr>
                      <th style={S.th}>#</th><th style={S.th}>Name</th><th style={S.th}>Alias</th>
                      <th style={S.th}>RAD</th><th style={S.th}>Year</th><th style={S.th}>Comment</th>
                      <th style={S.th}>Actions</th>
                    </tr></thead>
                    <tbody>
                      {rosterData.samples.map((s, i) => (
                        <tr key={i} style={{ background: i % 2 ? '#f9f9f9' : '#fff' }}>
                          <td style={S.td}>{(rosterData.page - 1) * rosterData.page_size + i + 1}</td>
                          <td style={{ ...S.td, fontWeight: 600 }}>{s.name}</td>
                          <td style={S.td}>{s.alias || '-'}</td>
                          <td style={S.td}>
                            {s.rad && <Badge text={s.rad + '-row'} color={s.rad === '2' ? C.blue : C.orange} />}
                          </td>
                          <td style={S.td}>{s.year || '-'}</td>
                          <td style={{ ...S.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.kommentar || '-'}
                          </td>
                          <td style={S.td}>
                            <button onClick={() => { setActiveTab('access'); lookupSample(s.name); }}
                              style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                              View {'\u2192'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination data={rosterData} onPageChange={p => loadRoster(p, rosterSearch, rosterRad, rosterYear)} />
              </>
            )}
          </Section>
        </>
      )}
    </div>
  );

  /* ======== 2. DATA ACCESS ======== */
  const renderDataAccess = () => (
    <div>
      {/* Quick Point Lookup */}
      <Section title="Quick Point Lookup" icon={'\u26A1'}>
        <p style={S.desc}>
          Instantly look up a single genotype call: <strong>Cultivar × Marker → Allele call</strong>.
          The fastest way to check a specific data point.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'end' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={S.label}>Sample / Cultivar</label>
            <input value={pointSample} onChange={e => setPointSample(e.target.value)}
              placeholder="e.g. Barke" style={{ ...S.input, maxWidth: 280 }}
              onKeyDown={e => e.key === 'Enter' && handlePointLookup()} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={S.label}>Marker</label>
            <input value={pointMarker} onChange={e => setPointMarker(e.target.value)}
              placeholder="e.g. bopa1_1670_369" style={{ ...S.input, maxWidth: 280 }}
              onKeyDown={e => e.key === 'Enter' && handlePointLookup()} />
          </div>
          <Btn disabled={!selectedTable || !pointSample.trim() || !pointMarker.trim() || loading}
            onClick={handlePointLookup}>
            {loading ? 'Looking up...' : '\u26A1 Lookup'}
          </Btn>
        </div>
        {pointResult && (
          <div style={{
            background: pointResult.type === 'missing' ? C.redLt : C.greenLt,
            border: '2px solid ' + (pointResult.type === 'missing' ? C.red : C.green),
            borderRadius: 12, padding: 20, textAlign: 'center', maxWidth: 500, margin: '0 auto',
          }}>
            <div style={{ fontSize: 13, color: C.gray, marginBottom: 8 }}>
              <strong>{pointResult.sample}</strong> × <strong style={{ fontFamily: 'monospace' }}>{pointResult.marker}</strong>
            </div>
            <div style={{
              fontSize: 48, fontWeight: 900, fontFamily: 'monospace',
              color: pointResult.type === 'missing' ? C.red : pointResult.type === 'het' ? C.purple : C.accent,
              marginBottom: 8,
            }}>
              {pointResult.call || '\u2014'}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', fontSize: 13 }}>
              <Badge text={pointResult.type.toUpperCase()}
                color={pointResult.type === 'het' ? C.purple : pointResult.type === 'missing' ? C.red : C.green} />
              {pointResult.rad && <Badge text={pointResult.rad + '-row'}
                color={pointResult.rad === '2' ? C.blue : C.orange} />}
              {pointResult.year && <span style={{ color: C.gray }}>Year: {pointResult.year}</span>}
            </div>
          </div>
        )}
      </Section>

      {/* Search */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 20 }}>
        <Section title="Search Markers" icon={'\u{1F52C}'}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={markerSearchQ} placeholder="Type marker name (e.g. bopa1, bk_01)..."
              onChange={e => { setMarkerSearchQ(e.target.value); debounceSearch(searchMarkers, e.target.value); }}
              style={{ ...S.input, flex: 1, maxWidth: 350 }} />
          </div>
          {markerSearchRes.length > 0 && (
            <div style={S.searchResults}>
              {markerSearchRes.map(m => (
                <div key={m} style={S.searchItem}
                  onClick={() => { lookupMarker(m); setMarkerSearchRes([]); setMarkerSearchQ(m); }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{m}</span>
                  <span style={{ color: C.accent, fontSize: 12 }}>{'\u2192'} View</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Search Samples" icon={'\u{1F331}'}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={sampleSearchQ} placeholder="Type sample / cultivar name..."
              onChange={e => { setSampleSearchQ(e.target.value); debounceSearch(searchSamples, e.target.value); }}
              style={{ ...S.input, flex: 1, maxWidth: 350 }} />
          </div>
          {sampleSearchRes.length > 0 && (
            <div style={S.searchResults}>
              {sampleSearchRes.map(s => (
                <div key={s.sample_id} style={S.searchItem}
                  onClick={() => { lookupSample(s.sample_id); setSampleSearchRes([]); setSampleSearchQ(s.original_name); }}>
                  <div>
                    <strong>{s.original_name}</strong>
                    {s.alias_name && <span style={{ color: '#999', marginLeft: 6 }}>({s.alias_name})</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {s.rad && <Badge text={s.rad + '-row'} color={s.rad === '2' ? C.blue : C.orange} />}
                    {s.year && <span style={{ fontSize: 11, color: C.gray }}>{s.year}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Marker Lookup Result */}
      {markerLookup && (
        <Section title={'Marker: ' + markerLookup.marker} icon={'\u{1F52C}'}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <CopyBtn copied={copied}
              onClick={() => flashCopy(['Sample', 'RAD', 'Call'],
                markerLookup.sample_calls.map(sc => [sc.sample, sc.rad, sc.call || 'MISSING']))} />
          </div>
          <div style={S.cardGrid}>
            <StatCard label="Total Samples" value={markerLookup.total_samples} />
            <StatCard label="Call Rate" value={((markerLookup.call_rate * 100).toFixed(1)) + '%'}
              color={markerLookup.call_rate > 0.95 ? C.green : C.orange} />
            <StatCard label="MAF" value={markerLookup.maf.toFixed(3)} color={C.blue} />
            <StatCard label="Het Rate" value={((markerLookup.het_rate * 100).toFixed(1)) + '%'} color={C.purple} />
          </div>
          <h4 style={{ marginTop: 16 }}>Allele Distribution</h4>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            {markerLookup.alleles.map(a => (
              <div key={a.allele} style={{
                background: a.allele.length === 1 && HET_CODES.has(a.allele) ? C.purpleLt : C.accentLt,
                padding: '10px 16px', borderRadius: 8, textAlign: 'center', minWidth: 70,
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'monospace' }}>{a.allele}</div>
                <div style={{ fontSize: 12 }}>{a.count.toLocaleString()} ({(a.frequency * 100).toFixed(1)}%)</div>
              </div>
            ))}
          </div>
          {Object.keys(markerLookup.rad_breakdown).length > 1 && (
            <>
              <h4>Per Row-Type Breakdown</h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={S.table}>
                  <thead><tr>
                    <th style={S.th}>RAD</th>
                    <th style={S.th}>Samples</th>
                    {markerLookup.alleles.map(a => <th key={a.allele} style={S.th}>{a.allele}</th>)}
                  </tr></thead>
                  <tbody>
                    {Object.entries(markerLookup.rad_breakdown).map(([rad, info], i) => (
                      <tr key={rad} style={{ background: i % 2 ? '#f9f9f9' : '#fff' }}>
                        <td style={{ ...S.td, fontWeight: 700 }}>{rad}-row</td>
                        <td style={S.td}>{info.total}</td>
                        {markerLookup.alleles.map(a => (
                          <td key={a.allele} style={S.td}>{info.alleles[a.allele] || 0}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <h4>Sample Calls (first {markerLookup.sample_calls.length})</h4>
          <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Sample</th><th style={S.th}>RAD</th><th style={S.th}>Call</th></tr></thead>
              <tbody>
                {markerLookup.sample_calls.map((sc, i) => (
                  <tr key={i} style={{ background: !sc.call ? C.redLt : i % 2 ? '#f9f9f9' : '#fff' }}>
                    <td style={S.td}>{sc.sample}</td>
                    <td style={S.td}>{sc.rad}</td>
                    <td style={{ ...S.td, fontWeight: 700, fontFamily: 'monospace' }}>
                      {sc.call || <span style={{ color: C.red }}>MISSING</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Btn variant="ghost" onClick={() => setMarkerLookup(null)} style={{ marginTop: 12 }}>Close</Btn>
        </Section>
      )}

      {/* Sample Lookup Result */}
      {sampleLookup && (
        <Section title={'Sample: ' + sampleLookup.metadata.original_name} icon={'\u{1F331}'}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <CopyBtn copied={copied}
              onClick={() => flashCopy(['Marker', 'Call', 'Type'],
                sampleLookup.marker_calls.map(mc => [mc.marker, mc.call || '-', mc.type]))} />
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, fontSize: 14 }}>
            {sampleLookup.metadata.alias_name && (
              <span><strong>Alias:</strong> {sampleLookup.metadata.alias_name}</span>
            )}
            <span><strong>RAD:</strong> <Badge text={sampleLookup.metadata.rad + '-row'}
              color={sampleLookup.metadata.rad === '2' ? C.blue : C.orange} /></span>
            <span><strong>Year:</strong> {sampleLookup.metadata.year}</span>
          </div>
          <div style={S.cardGrid}>
            <StatCard label="Call Rate" value={((sampleLookup.stats.call_rate * 100).toFixed(1)) + '%'}
              color={sampleLookup.stats.call_rate > 0.95 ? C.green : C.orange} />
            <StatCard label="Het Rate" value={((sampleLookup.stats.het_rate * 100).toFixed(1)) + '%'} color={C.purple} />
            <StatCard label="Valid Calls" value={sampleLookup.stats.valid_calls} color={C.blue} />
            <StatCard label="Missing" value={sampleLookup.stats.missing_calls} color={C.red} />
          </div>
          <h4>Allele Distribution</h4>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {Object.entries(sampleLookup.allele_distribution).slice(0, 10).map(([allele, count]) => (
              <div key={allele} style={{
                background: HET_CODES.has(allele) ? C.purpleLt : C.accentLt,
                padding: '8px 14px', borderRadius: 8, textAlign: 'center',
              }}>
                <div style={{ fontWeight: 800, fontFamily: 'monospace', fontSize: 16 }}>{allele}</div>
                <div style={{ fontSize: 11 }}>{count.toLocaleString()}</div>
              </div>
            ))}
          </div>
          <h4>Marker Calls (first {Math.min(sampleLookup.marker_calls.length, 200)})</h4>
          <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Marker</th><th style={S.th}>Call</th><th style={S.th}>Type</th></tr></thead>
              <tbody>
                {sampleLookup.marker_calls.slice(0, 200).map((mc, i) => (
                  <tr key={i} style={{
                    background: mc.type === 'missing' ? C.redLt : mc.type === 'het' ? C.purpleLt : i % 2 ? '#f9f9f9' : '#fff',
                  }}>
                    <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{mc.marker}</td>
                    <td style={{ ...S.td, fontWeight: 700, fontFamily: 'monospace' }}>
                      {mc.call || <span style={{ color: C.red }}>-</span>}
                    </td>
                    <td style={S.td}><Badge text={mc.type}
                      color={mc.type === 'het' ? C.purple : mc.type === 'missing' ? C.red : C.green} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Btn variant="ghost" onClick={() => setSampleLookup(null)} style={{ marginTop: 12 }}>Close</Btn>
        </Section>
      )}

      {/* Marker Browser */}
      <Section title="Marker Browser" icon={'\u{1F4D6}'} collapsible defaultOpen={false}>
        <p style={S.desc}>Browse markers grouped by prefix. Click a prefix to filter, or search by name.</p>
        {!markerPrefixes && (
          <Btn disabled={!selectedTable || loading} onClick={loadPrefixes}>
            {loading ? 'Loading...' : '\u{1F4D6} Load Marker Prefixes'}
          </Btn>
        )}
        {markerPrefixes && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              <button onClick={() => { setBrowsePrefix(''); loadBrowseMarkers(1, '', browseSearch); }}
                style={{
                  background: !browsePrefix ? C.accent : C.grayLt, color: !browsePrefix ? '#fff' : C.gray,
                  border: 'none', borderRadius: 20, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                }}>All ({markerPrefixes.total_markers.toLocaleString()})</button>
              {markerPrefixes.prefixes.slice(0, 20).map(p => (
                <button key={p.prefix} onClick={() => { setBrowsePrefix(p.prefix); loadBrowseMarkers(1, p.prefix, browseSearch); }}
                  style={{
                    background: browsePrefix === p.prefix ? C.accent : C.accentLt,
                    color: browsePrefix === p.prefix ? '#fff' : C.accent,
                    border: 'none', borderRadius: 20, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  }}>
                  {p.prefix} ({p.count.toLocaleString()})
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'end' }}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Search markers</label>
                <input value={browseSearch} onChange={e => setBrowseSearch(e.target.value)}
                  placeholder="Type to filter..."
                  onKeyDown={e => e.key === 'Enter' && loadBrowseMarkers(1, browsePrefix, browseSearch)}
                  style={{ ...S.input, maxWidth: 300 }} />
              </div>
              <Btn disabled={loading} onClick={() => loadBrowseMarkers(1, browsePrefix, browseSearch)}
                variant="outline" style={{ padding: '9px 18px' }}>
                {'\u{1F50D}'} Filter
              </Btn>
            </div>
            {browseData && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: C.gray }}>{browseData.total.toLocaleString()} markers</span>
                  <CopyBtn copied={copied} onClick={() => flashCopy(['Marker'], browseData.markers.map(m => [m]))} />
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 300, overflowY: 'auto' }}>
                  {browseData.markers.map(m => (
                    <span key={m} onClick={() => lookupMarker(m)}
                      style={{
                        background: C.accentLt, padding: '4px 10px', borderRadius: 6,
                        fontFamily: 'monospace', fontSize: 12, cursor: 'pointer',
                        transition: 'background .15s',
                      }}
                      onMouseEnter={e => e.target.style.background = '#c8e6c9'}
                      onMouseLeave={e => e.target.style.background = C.accentLt}
                    >{m}</span>
                  ))}
                </div>
                <Pagination data={browseData} onPageChange={p => loadBrowseMarkers(p, browsePrefix, browseSearch)} />
              </>
            )}
          </>
        )}
      </Section>

      {/* Download Subset */}
      <Section title="Download Custom Subset" icon={'\u2B07\uFE0F'} collapsible>
        <p style={{ color: C.gray, fontSize: 13, marginBottom: 12 }}>
          Specify markers and/or samples to download a custom data extract as CSV.
          Leave a field empty to include all.
        </p>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <label style={S.label}>Marker Names <span style={{ fontWeight: 400, color: '#999' }}>(comma or newline separated, empty = all)</span></label>
            <textarea value={dlMarkerInput} onChange={e => setDlMarkerInput(e.target.value)}
              rows={3} placeholder="bopa1_1670_369, bk_01, ..."
              style={{ ...S.input, resize: 'vertical', minHeight: 60 }} />
          </div>
          <div style={{ flex: 1, minWidth: 280 }}>
            <label style={S.label}>Sample Names <span style={{ fontWeight: 400, color: '#999' }}>(comma or newline separated, empty = all)</span></label>
            <textarea value={dlSampleInput} onChange={e => setDlSampleInput(e.target.value)}
              rows={3} placeholder="Barke, GN255751, ..."
              style={{ ...S.input, resize: 'vertical', minHeight: 60 }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <label style={S.label}>RAD Filter:</label>
          <select value={dlRadFilter} onChange={e => setDlRadFilter(e.target.value)} style={{ ...S.select, maxWidth: 150 }}>
            <option value="">All</option>
            <option value="2">2-Row</option>
            <option value="6">6-Row</option>
          </select>
        </div>
        <Btn onClick={handleSubsetDownload} disabled={downloading || !selectedTable} variant="blue">
          {downloading ? 'Downloading...' : '\u2B07\uFE0F Download CSV'}
        </Btn>
      </Section>
    </div>
  );

  /* ======== 3. COMPARE ======== */
  const renderCompare = () => (
    <div>
      <Section title="Side-by-Side Cultivar Comparison" icon={'\u{1F504}'}>
        <p style={S.desc}>
          Compare <strong>2–10 cultivars</strong> marker by marker. See which markers match and which differ.
          Toggle <strong>"Show Only Differences"</strong> to quickly find distinguishing markers.
        </p>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, alignItems: 'end' }}>
          <div style={{ flex: 1, minWidth: 300 }}>
            <label style={S.label}>Sample Names (comma or newline separated, 2–10)</label>
            <textarea value={compareSamplesInput} onChange={e => setCompareSamplesInput(e.target.value)}
              rows={3} placeholder="Barke, Scarlett, Morex..."
              style={{ ...S.input, resize: 'vertical', minHeight: 60 }} />
          </div>
          <div>
            <label style={S.label}>Max Markers</label>
            <input type="number" value={compareMax} onChange={e => setCompareMax(Number(e.target.value))}
              min={100} max={20000} step={500} style={{ ...S.input, maxWidth: 140 }} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" checked={diffMode} onChange={e => setDiffMode(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: C.accent }} />
            <strong>Show Only Differences</strong>
            <span style={{ color: C.gray, fontSize: 12 }}>(difference finder mode)</span>
          </label>
          <Btn disabled={!selectedTable || !compareSamplesInput.trim() || loading} onClick={handleCompare}>
            {loading ? 'Comparing...' : diffMode ? '\u{1F50D} Find Differences' : '\u{1F504} Compare'}
          </Btn>
        </div>
      </Section>

      {compareResults && (
        <Section title="Comparison Results" icon={'\u{1F4CA}'}>
          {/* Sample info */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            {compareResults.sample_info.map((s, i) => (
              <div key={i} style={{
                background: C.accentLt, padding: '10px 16px', borderRadius: 10,
                textAlign: 'center', minWidth: 120,
              }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{s.name}</div>
                {s.alias && <div style={{ fontSize: 11, color: C.gray }}>{s.alias}</div>}
                <div style={{ marginTop: 4 }}>
                  {s.rad && <Badge text={s.rad + '-row'} color={s.rad === '2' ? C.blue : C.orange} />}
                </div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div style={S.cardGrid}>
            <StatCard label="Total Markers" value={compareResults.total_markers} icon={'\u{1F52C}'} />
            <StatCard label="Matching" value={compareResults.matching_markers} color={C.green} icon={'\u2705'} />
            <StatCard label="Differing" value={compareResults.differing_markers}
              color={compareResults.differing_markers > 0 ? C.red : C.green} icon={'\u274C'} />
            <StatCard label="Concordance" value={compareResults.concordance_pct + '%'}
              color={compareResults.concordance_pct > 95 ? C.green : C.orange} icon={'\u{1F3AF}'} />
          </div>

          {compareResults.not_found.length > 0 && (
            <div style={{ ...S.alert, background: C.orangeLt, marginBottom: 16 }}>
              {'\u26A0\uFE0F'} Not found: <strong>{compareResults.not_found.join(', ')}</strong>
            </div>
          )}

          {/* Concordance bar */}
          <div style={{ background: C.grayLt, borderRadius: 8, height: 24, marginBottom: 20, overflow: 'hidden', position: 'relative' }}>
            <div style={{
              width: compareResults.concordance_pct + '%', height: '100%',
              background: compareResults.concordance_pct > 95 ? C.green : compareResults.concordance_pct > 80 ? C.orange : C.red,
              borderRadius: 8, transition: 'width .5s',
            }} />
            <span style={{ position: 'absolute', top: 3, left: '50%', transform: 'translateX(-50%)', fontSize: 13, fontWeight: 700 }}>
              {compareResults.concordance_pct}% Match
            </span>
          </div>

          {/* Table */}
          {compareResults.rows.length > 0 && (() => {
            const sampleNames = compareResults.sample_info.map(s => s.name);
            const headers = ['Marker', ...sampleNames, 'Match'];
            const tableRows = compareResults.rows.map(r => [
              r.marker,
              ...sampleNames.map(n => r.calls[n] || '-'),
              r.match ? 'YES' : 'NO',
            ]);
            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, color: C.gray }}>
                    {diffMode ? 'Showing only differences' : 'All markers'}: <strong>{compareResults.rows.length.toLocaleString()}</strong> rows
                  </span>
                  <CopyBtn copied={copied} onClick={() => flashCopy(headers, tableRows)} />
                </div>
                <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
                  <table style={S.table}>
                    <thead><tr>
                      <th style={S.th}>Marker</th>
                      {sampleNames.map(n => <th key={n} style={S.th}>{n}</th>)}
                      <th style={S.th}>Match</th>
                    </tr></thead>
                    <tbody>
                      {compareResults.rows.slice(0, 500).map((r, i) => (
                        <tr key={i} style={{ background: r.match ? (i % 2 ? '#f9f9f9' : '#fff') : C.redLt }}>
                          <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{r.marker}</td>
                          {sampleNames.map(n => (
                            <td key={n} style={{
                              ...S.td, fontWeight: 700, fontFamily: 'monospace',
                              color: !r.calls[n] ? C.red : HET_CODES.has(r.calls[n]) ? C.purple : C.accent,
                            }}>
                              {r.calls[n] || '-'}
                            </td>
                          ))}
                          <td style={S.td}>
                            {r.match
                              ? <span style={{ color: C.green }}>{'\u2705'}</span>
                              : <span style={{ color: C.red, fontWeight: 700 }}>{'\u274C'}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {compareResults.rows.length > 500 && (
                    <p style={{ padding: 8, color: C.gray, fontSize: 12 }}>
                      Showing first 500 of {compareResults.rows.length.toLocaleString()} rows.
                      Use "Show Only Differences" to narrow down.
                    </p>
                  )}
                </div>
              </>
            );
          })()}
        </Section>
      )}
    </div>
  );

  /* ======== 4. QUALITY CONTROL ======== */
  const renderQC = () => (
    <div>
      <Section title="Sample Quality Profiles" icon={'\u{1F9EC}'}>
        <p style={S.desc}>Per-sample call rate, heterozygosity, and missing data. Samples with extreme values are automatically <strong>flagged</strong>.</p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'end', marginBottom: 12 }}>
          <div>
            <label style={S.label}>Max Markers</label>
            <input type="number" value={profMax} onChange={e => setProfMax(Number(e.target.value))}
              min={100} max={20000} step={500} style={{ ...S.input, maxWidth: 140 }} />
          </div>
          <Btn disabled={!selectedTable || loading}
            onClick={() => run('/api/snp-tools/genotype-profiles',
              { table_name: selectedTable, max_markers: profMax }, setProfResults)}>
            {loading ? 'Profiling...' : '\u{1F9EC} Generate Profiles'}
          </Btn>
        </div>
        {profResults && (
          <>
            <div style={S.cardGrid}>
              <StatCard label="Samples" value={profResults.summary.total_samples} />
              <StatCard label="Mean Call Rate" value={((profResults.summary.mean_call_rate * 100).toFixed(1)) + '%'}
                color={profResults.summary.mean_call_rate > 0.95 ? C.green : C.orange} />
              <StatCard label="Mean Het Rate" value={((profResults.summary.mean_het_rate * 100).toFixed(1)) + '%'} color={C.purple} />
              <StatCard label="Flagged Samples" value={profResults.summary.flagged_samples}
                color={profResults.summary.flagged_samples > 0 ? C.red : C.green} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <CopyBtn copied={copied}
                onClick={() => flashCopy(['Sample', 'RAD', 'Year', 'Call Rate', 'Het Rate', 'Valid', 'Missing', 'Flags'],
                  profResults.profiles.map(p => [
                    p.original_name || p.sample_id, p.rad, p.year,
                    (p.call_rate * 100).toFixed(1) + '%', (p.het_rate * 100).toFixed(1) + '%',
                    p.valid_calls, p.missing_calls, p.flags.join(', ') || '-',
                  ]))} />
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto', marginTop: 4 }}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Sample</th><th style={S.th}>RAD</th><th style={S.th}>Year</th>
                  <th style={S.th}>Call Rate</th><th style={S.th}>Het Rate</th>
                  <th style={S.th}>Valid</th><th style={S.th}>Missing</th><th style={S.th}>Flags</th>
                </tr></thead>
                <tbody>
                  {profResults.profiles.map((p, i) => (
                    <tr key={i} style={{ background: p.flags.length ? C.redLt : i % 2 ? '#f9f9f9' : '#fff' }}>
                      <td style={S.td}>{p.original_name || p.sample_id}</td>
                      <td style={S.td}>{p.rad}</td>
                      <td style={S.td}>{p.year}</td>
                      <td style={S.td}>
                        <span style={{
                          fontWeight: 700,
                          color: p.call_rate < 0.8 ? C.red : p.call_rate < 0.95 ? C.orange : C.green,
                        }}>{(p.call_rate * 100).toFixed(1)}%</span>
                      </td>
                      <td style={S.td}>{(p.het_rate * 100).toFixed(1)}%</td>
                      <td style={S.td}>{p.valid_calls.toLocaleString()}</td>
                      <td style={S.td}>{p.missing_calls.toLocaleString()}</td>
                      <td style={S.td}>
                        {p.flags.length ? p.flags.map(f => (
                          <Badge key={f} text={f.replace(/_/g, ' ')} color={C.red} />
                        )) : <span style={{ color: '#ccc' }}>-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Section>

      <Section title="Missing Data Overview" icon={'\u{1F573}\uFE0F'} collapsible>
        <Btn disabled={!selectedTable || loading}
          onClick={() => run('/api/snp-tools/missing-rate-summary',
            { table_name: selectedTable, max_markers: profMax }, setMissingResults)}>
          {loading ? 'Analyzing...' : '\u{1F573}\uFE0F Analyze Missing Data'}
        </Btn>
        {missingResults && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 300 }}>
                <h4>Per-Marker Missing Rate</h4>
                <p style={{ fontSize: 13, color: C.gray }}>
                  Mean: {(missingResults.marker_summary.mean_missing_rate * 100).toFixed(2)}% |
                  Median: {(missingResults.marker_summary.median_missing_rate * 100).toFixed(2)}%
                </p>
                <BarChart data={missingResults.marker_summary.histogram} />
                {missingResults.marker_summary.worst_markers.length > 0 && (
                  <>
                    <h5>Worst Markers</h5>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {missingResults.marker_summary.worst_markers.slice(0, 15).map((m, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', fontSize: 12, background: i % 2 ? '#f9f9f9' : '#fff' }}>
                          <span style={{ fontFamily: 'monospace' }}>{m.marker}</span>
                          <span style={{ color: C.red, fontWeight: 600 }}>{(m.rate * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 300 }}>
                <h4>Per-Sample Missing Rate</h4>
                <p style={{ fontSize: 13, color: C.gray }}>
                  Mean: {(missingResults.sample_summary.mean_missing_rate * 100).toFixed(2)}% |
                  Median: {(missingResults.sample_summary.median_missing_rate * 100).toFixed(2)}%
                </p>
                <BarChart data={missingResults.sample_summary.histogram} />
                {missingResults.sample_summary.worst_samples.length > 0 && (
                  <>
                    <h5>Worst Samples</h5>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {missingResults.sample_summary.worst_samples.slice(0, 15).map((s, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', fontSize: 12, background: i % 2 ? '#f9f9f9' : '#fff' }}>
                          <span>{s.sample} {s.rad && <Badge text={s.rad + 'r'} color={s.rad === '2' ? C.blue : C.orange} />}</span>
                          <span style={{ color: C.red, fontWeight: 600 }}>{(s.rate * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </Section>

      <Section title="MAF Distribution" icon={'\u{1F4CA}'} collapsible>
        <Btn disabled={!selectedTable || loading}
          onClick={() => run('/api/snp-tools/maf-distribution',
            { table_name: selectedTable, max_markers: profMax }, setMafResults)}>
          {loading ? 'Computing...' : '\u{1F4CA} Compute MAF'}
        </Btn>
        {mafResults && (
          <div style={{ marginTop: 16 }}>
            <div style={S.cardGrid}>
              <StatCard label="Markers" value={mafResults.total_markers} />
              <StatCard label="Mean MAF" value={mafResults.mean_maf.toFixed(3)} color={C.blue} />
              <StatCard label="Median MAF" value={mafResults.median_maf.toFixed(3)} color={C.purple} />
            </div>
            <h4>MAF Histogram</h4>
            <BarChart data={mafResults.histogram} maxWidth={500} />
            {mafResults.markers.length > 0 && (
              <>
                <h4>Low-MAF Markers (potential monomorphic)</h4>
                <div style={{ overflowX: 'auto', maxHeight: 250, overflowY: 'auto' }}>
                  <table style={S.table}>
                    <thead><tr><th style={S.th}>#</th><th style={S.th}>Marker</th><th style={S.th}>MAF</th></tr></thead>
                    <tbody>
                      {mafResults.markers.slice(0, 50).map((m, i) => (
                        <tr key={i} style={{ background: m.maf < 0.01 ? C.redLt : i % 2 ? '#f9f9f9' : '#fff' }}>
                          <td style={S.td}>{i + 1}</td>
                          <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{m.marker}</td>
                          <td style={S.td}>{m.maf.toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </Section>

      {/* Diversity Stats (moved from Population) */}
      <Section title="Genetic Diversity Statistics" icon={'\u{1F30D}'} collapsible>
        <p style={S.desc}>Observed heterozygosity (Ho), PIC, polymorphic rate, and allele count distribution.</p>
        <Btn disabled={!selectedTable || loading}
          onClick={() => run('/api/snp-tools/diversity-stats',
            { table_name: selectedTable, max_markers: 5000 }, setDivResults)}>
          {loading ? 'Computing...' : '\u{1F30D} Compute Diversity'}
        </Btn>
        {divResults && (
          <div style={{ marginTop: 16 }}>
            <div style={S.cardGrid}>
              <StatCard label="Samples" value={divResults.summary.total_samples} />
              <StatCard label="Markers" value={divResults.summary.total_markers_analyzed} />
              <StatCard label="Polymorphic" value={((divResults.summary.polymorphic_rate * 100).toFixed(1)) + '%'} color={C.green} />
              <StatCard label="Mean Ho" value={divResults.summary.mean_ho.toFixed(3)} color={C.blue} />
              <StatCard label="Mean PIC" value={divResults.summary.mean_pic.toFixed(3)} color={C.purple} />
            </div>
            <h4>Allele Count Distribution</h4>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              {Object.entries(divResults.summary.allele_count_distribution)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([k, v]) => (
                  <div key={k} style={{ background: C.accentLt, padding: '10px 16px', borderRadius: 8, textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: C.accent }}>{v.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: C.gray }}>{k}-allele markers</div>
                  </div>
                ))}
            </div>
            <h4>Top Informative Markers (by PIC)</h4>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
              <CopyBtn copied={copied}
                onClick={() => flashCopy(['#', 'Marker', 'PIC', 'Ho', 'MAF', 'Missing%'],
                  divResults.marker_details.slice(0, 80).map((m, i) => [
                    i + 1, m.marker, m.pic.toFixed(3), m.ho.toFixed(3), m.maf.toFixed(3), (m.missing_rate * 100).toFixed(1) + '%',
                  ]))} />
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>#</th><th style={S.th}>Marker</th><th style={S.th}>PIC</th>
                  <th style={S.th}>Ho</th><th style={S.th}>MAF</th><th style={S.th}>Missing%</th>
                </tr></thead>
                <tbody>
                  {divResults.marker_details.slice(0, 80).map((m, i) => (
                    <tr key={i} style={{ background: i % 2 ? '#f9f9f9' : '#fff' }}>
                      <td style={S.td}>{i + 1}</td>
                      <td style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}>{m.marker}</td>
                      <td style={S.td}><strong>{m.pic.toFixed(3)}</strong></td>
                      <td style={S.td}>{m.ho.toFixed(3)}</td>
                      <td style={S.td}>{m.maf.toFixed(3)}</td>
                      <td style={S.td}>{(m.missing_rate * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>

      {/* Marker Concordance (moved from Population) */}
      <Section title="Marker Concordance Check" icon={'\u{1F3AF}'} collapsible>
        <p style={S.desc}>Enter diagnostic / selection markers to see allele distribution. For disease resistance, quality traits, etc.</p>
        <textarea value={concMarkerInput} onChange={e => setConcMarkerInput(e.target.value)}
          rows={3} placeholder="Paste marker names (comma or newline separated)..."
          style={{ ...S.input, resize: 'vertical', minHeight: 60, marginBottom: 12 }} />
        <Btn disabled={!selectedTable || !concMarkerInput.trim() || loading}
          onClick={() => {
            const mks = concMarkerInput.split(/[,\n]+/).map(s => s.trim()).filter(Boolean);
            run('/api/snp-tools/marker-concordance', { table_name: selectedTable, marker_names: mks }, setConcResults);
          }}>
          {loading ? 'Checking...' : '\u{1F3AF} Check Concordance'}
        </Btn>
        {concResults && (
          <div style={{ marginTop: 16 }}>
            {concResults.markers_not_found.length > 0 && (
              <div style={{ ...S.alert, background: C.orangeLt, marginBottom: 12 }}>
                Not found: <strong>{concResults.markers_not_found.join(', ')}</strong>
              </div>
            )}
            {concResults.results.map((r, idx) => (
              <div key={idx} style={{ background: '#fff', border: '1px solid ' + C.border, borderRadius: 8, padding: 16, marginBottom: 12 }}>
                <h4 style={{ margin: '0 0 8px', fontFamily: 'monospace' }}>
                  {r.marker}
                  <span style={{ fontWeight: 400, fontSize: 13, marginLeft: 12, color: C.gray }}>
                    Call: {(r.call_rate * 100).toFixed(1)}% | Het: {(r.het_rate * 100).toFixed(1)}%
                  </span>
                </h4>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {r.alleles.map(a => (
                    <div key={a.allele} style={{
                      background: HET_CODES.has(a.allele) ? C.purpleLt : C.accentLt,
                      padding: '8px 14px', borderRadius: 8, textAlign: 'center',
                    }}>
                      <div style={{ fontWeight: 800, fontFamily: 'monospace', fontSize: 18 }}>{a.allele}</div>
                      <div style={{ fontSize: 11 }}>{a.count} ({(a.frequency * 100).toFixed(1)}%)</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );

  /* ======== 5. DUPLICATES ======== */
  const renderDuplicates = () => (
    <div>
      <Section title="Find Duplicates Within Table" icon={'\u{1F501}'}>
        <p style={S.desc}>Identify genotypes with near-identical marker profiles within a single table.</p>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, alignItems: 'end' }}>
          <div>
            <label style={S.label}>Threshold (%)</label>
            <input type="number" value={dupThreshold} onChange={e => setDupThreshold(Number(e.target.value))}
              min={0.5} max={1.0} step={0.01} style={{ ...S.input, maxWidth: 120 }} />
          </div>
          <div>
            <label style={S.label}>Max Markers</label>
            <input type="number" value={dupMaxMarkers} onChange={e => setDupMaxMarkers(Number(e.target.value))}
              min={100} max={10000} step={500} style={{ ...S.input, maxWidth: 140 }} />
          </div>
          <Btn disabled={!selectedTable || loading}
            onClick={() => run('/api/snp-tools/find-duplicates',
              { table_name: selectedTable, threshold: dupThreshold, max_markers: dupMaxMarkers }, setDupResults)}>
            {loading ? 'Scanning...' : '\u{1F50D} Find Duplicates'}
          </Btn>
        </div>
        {dupResults && (
          <>
            <div style={S.cardGrid}>
              <StatCard label="Samples Compared" value={dupResults.total_samples} />
              <StatCard label="Markers Used" value={dupResults.markers_compared} />
              <StatCard label="Duplicate Pairs" value={dupResults.duplicates.length}
                color={dupResults.duplicates.length > 0 ? C.red : C.green} />
            </div>
            {dupResults.duplicates.length === 0 ? (
              <div style={{ ...S.alert, background: C.greenLt, marginTop: 12 }}>
                {'\u2705'} No duplicates found above {(dupThreshold * 100).toFixed(0)}% threshold.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <CopyBtn copied={copied}
                    onClick={() => flashCopy(['Sample A', 'Sample B', 'Match %', 'Matching', 'Compared', 'Mismatched Markers'],
                      dupResults.duplicates.map(d => [
                        d.sample_a, d.sample_b, (d.match_ratio * 100).toFixed(1) + '%',
                        d.matching_markers, d.compared_markers, d.mismatched_markers.join(', ') || '-',
                      ]))} />
                </div>
                <div style={{ overflowX: 'auto', maxHeight: 400, marginTop: 4 }}>
                  <table style={S.table}>
                    <thead><tr>
                      <th style={S.th}>Sample A</th><th style={S.th}>Sample B</th>
                      <th style={S.th}>Match %</th><th style={S.th}>Matching</th>
                      <th style={S.th}>Compared</th><th style={S.th}>Mismatched Markers</th>
                    </tr></thead>
                    <tbody>
                      {dupResults.duplicates.slice(0, 200).map((d, i) => (
                        <tr key={i} style={{ background: d.match_ratio >= 0.99 ? C.redLt : i % 2 ? '#f9f9f9' : '#fff' }}>
                          <td style={S.td}>{d.sample_a}</td>
                          <td style={S.td}>{d.sample_b}</td>
                          <td style={S.td}>
                            <strong>{(d.match_ratio * 100).toFixed(1)}%</strong>
                            {d.match_ratio >= 0.99 && <Badge text="EXACT" color={C.red} />}
                          </td>
                          <td style={S.td}>{d.matching_markers.toLocaleString()}</td>
                          <td style={S.td}>{d.compared_markers.toLocaleString()}</td>
                          <td style={{ ...S.td, fontSize: 11, maxWidth: 300 }}>
                            {d.mismatched_markers.length ? d.mismatched_markers.join(', ') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {dupResults.duplicates.length > 200 && (
                    <p style={{ padding: 8, color: C.gray, fontSize: 12 }}>
                      Showing first 200 of {dupResults.duplicates.length} pairs.
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </Section>

      <Section title="Cross-Table Duplicate Check" icon={'\u{1F500}'} collapsible>
        <p style={S.desc}>Compare a <strong>source</strong> table against a <strong>target</strong> (master database) to see if any genotypes already exist.</p>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
          <TableSelect value={targetTable} onChange={setTargetTable} label="Target Table (Master DB)" width="350px" tables={tables} />
        </div>
        <Btn disabled={!selectedTable || !targetTable || loading}
          onClick={() => run('/api/snp-tools/cross-table-duplicates',
            { source_table: selectedTable, target_table: targetTable, threshold: dupThreshold, max_markers: dupMaxMarkers },
            setCrossResults)}>
          {loading ? 'Comparing...' : '\u{1F500} Check Cross-Table Duplicates'}
        </Btn>
        {crossResults && (
          <div style={{ marginTop: 16 }}>
            <div style={S.cardGrid}>
              <StatCard label="Source Samples" value={crossResults.source_samples} />
              <StatCard label="Target Samples" value={crossResults.target_samples} />
              <StatCard label="Common Markers" value={crossResults.common_markers} />
              <StatCard label="Duplicates Found" value={crossResults.duplicates_found}
                color={crossResults.duplicates_found > 0 ? C.red : C.green} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <CopyBtn copied={copied}
                onClick={() => flashCopy(['Source ID', 'Best Match', 'Match %', 'Status'],
                  crossResults.results.map(r => [
                    r.source_id, r.best_match_id || '-', (r.match_ratio * 100).toFixed(1) + '%',
                    r.is_duplicate ? 'DUPLICATE' : 'Unique',
                  ]))} />
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 400, marginTop: 4 }}>
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Source ID</th><th style={S.th}>Best Match</th>
                  <th style={S.th}>Match %</th><th style={S.th}>Status</th>
                </tr></thead>
                <tbody>
                  {crossResults.results.map((r, i) => (
                    <tr key={i} style={{ background: r.is_duplicate ? C.redLt : i % 2 ? '#f9f9f9' : '#fff' }}>
                      <td style={S.td}>{r.source_id}</td>
                      <td style={S.td}>{r.best_match_id || '-'}</td>
                      <td style={S.td}>{(r.match_ratio * 100).toFixed(1)}%</td>
                      <td style={S.td}>
                        {r.is_duplicate
                          ? <Badge text="DUPLICATE" color={C.red} />
                          : <Badge text="Unique" color={C.green} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>
    </div>
  );

  /* MAIN RENDER */
  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard':  return renderDashboard();
      case 'access':     return renderDataAccess();
      case 'compare':    return renderCompare();
      case 'qc':         return renderQC();
      case 'duplicates': return renderDuplicates();
      default:           return renderDashboard();
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 20, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 300 }}>
          <TableSelect value={selectedTable} onChange={setSelectedTable} label={'\u{1F4C2} Active Genotype Table'} tables={tables} />
        </div>
        {selectedTable && overview && (
          <div style={{ display: 'flex', gap: 12, fontSize: 13, color: C.gray }}>
            <span><strong>{overview.total_samples.toLocaleString()}</strong> samples</span>
            <span>|</span>
            <span><strong>{overview.total_markers.toLocaleString()}</strong> markers</span>
          </div>
        )}
      </div>

      <div style={{
        display: 'flex', gap: 4, marginBottom: 24,
        borderBottom: '3px solid #e0e0e0', paddingBottom: 2,
        overflowX: 'auto',
      }}>
        {TABS.map(t => (
          <button key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '10px 18px', border: 'none',
              borderBottom: activeTab === t.key ? '3px solid ' + C.accent : '3px solid transparent',
              background: activeTab === t.key ? C.accentLt : 'transparent',
              color: activeTab === t.key ? C.accent : C.gray,
              fontWeight: activeTab === t.key ? 700 : 500,
              cursor: 'pointer', fontSize: 14, transition: 'all .15s',
              whiteSpace: 'nowrap', borderRadius: '6px 6px 0 0',
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ ...S.alert, background: C.redLt, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>x</button>
        </div>
      )}

      {renderTab()}
    </div>
  );
};

export default SNPTools;
